import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Panel, Overline, Btn, Pill, Icon, PageLoader, SectionTitle } from "../../components/ui-kit";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

export default function ContentManager() {
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [tree, setTree] = useState(null);
  const [openCh, setOpenCh] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState("");
  const [mediaFor, setMediaFor] = useState(null);
  const [mediaVal, setMediaVal] = useState({ youtube: "", title: "", url: "" });

  const loadTree = (sid) => api.get(`/admin/content-tree${sid ? `?subject_id=${sid}` : ""}`).then(({ data }) => setTree(data));
  useEffect(() => {
    api.get("/academics/subjects").then(({ data }) => { setSubjects(data); setSubjectId(data[0]?.id || ""); });
    api.get("/admin/ai-settings").then(({ data }) => setSettings(data));
  }, []);
  useEffect(() => { if (subjectId) loadTree(subjectId); }, [subjectId]);

  const saveSettings = async () => {
    try { await api.put("/admin/ai-settings", settings); toast.success("AI parameters saved — future generations will use these."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const genSnapshot = async (tid) => {
    setBusy(tid);
    try { await api.post(`/admin/topic/${tid}/generate-snapshot`, {}); toast.success("Snapshot generated & stored"); loadTree(subjectId); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const addMedia = async (kind) => {
    setBusy(mediaFor);
    try {
      await api.post(`/admin/topic/${mediaFor}/media`, kind === "video" ? { kind, youtube: mediaVal.youtube, title: mediaVal.title } : { kind, url: mediaVal.url, title: mediaVal.title });
      toast.success(kind === "video" ? "Video linked to topic" : "Image added");
      setMediaFor(null); setMediaVal({ youtube: "", title: "", url: "" }); loadTree(subjectId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!settings) return <PageLoader />;
  const sub = subjects.find((s) => s.id === subjectId);
  const inputCls = "w-full px-3.5 py-2.5 rounded-xl border border-mim-line focus:border-mim-blue outline-none text-sm bg-white";

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="relative rounded-[28px] p-6 sm:p-7 text-white overflow-hidden" style={{ background: "linear-gradient(135deg,#8a6df0,#5f3fd0)" }}>
        <div className="absolute inset-0 dotgrid opacity-50" />
        <Icon name="Sparkles" className="absolute -right-4 -bottom-5 w-32 h-32 opacity-15" />
        <div className="relative"><Overline className="text-white/80">AI Content Studio</Overline><h1 className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight">Content & Generation</h1><p className="text-white/80 text-sm mt-1">Set global AI parameters, then auto-generate snapshots or attach videos & images to any topic.</p></div>
      </div>

      {/* Global AI parameters */}
      <Panel>
        <SectionTitle title="Global AI Parameters" icon="SlidersHorizontal" sub="Applied to every AI generation (snapshots, quizzes, worksheets)." />
        <textarea data-testid="global-prompt" value={settings.global_prompt} onChange={(e) => setSettings({ ...settings, global_prompt: e.target.value })} rows={4}
          placeholder="e.g. Follow CBSE Class 10 curriculum. Explain simply for a 15-year-old with Indian real-life examples." className={cn(inputCls, "resize-none")} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          <div><label className="text-xs font-semibold text-mim-navy block mb-1.5">Word count target</label><input type="number" value={settings.default_word_count} onChange={(e) => setSettings({ ...settings, default_word_count: Number(e.target.value) })} className={inputCls} /></div>
          <div><label className="text-xs font-semibold text-mim-navy block mb-1.5">Default difficulty</label><select value={settings.default_difficulty} onChange={(e) => setSettings({ ...settings, default_difficulty: e.target.value })} className={inputCls}><option>easy</option><option>medium</option><option>hard</option></select></div>
          <div><label className="text-xs font-semibold text-mim-navy block mb-1.5">Tone</label><select value={settings.tone} onChange={(e) => setSettings({ ...settings, tone: e.target.value })} className={inputCls}><option>friendly</option><option>formal</option><option>concise</option></select></div>
        </div>
        <div className="mt-3"><Btn onClick={saveSettings} icon="Save" data-testid="save-settings">Save parameters</Btn></div>
      </Panel>

      {/* Topic tree */}
      <div>
        <SectionTitle title="Topics" icon="FolderTree" action={
          <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} data-testid="cm-subject" className={cn(inputCls, "max-w-[200px]")}>{subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        } />
        {!tree ? <PageLoader /> : (
          <div className="space-y-3">
            {(tree.find((t) => t.id === subjectId)?.chapters || []).map((ch, ci) => (
              <Panel key={ch.id} className="p-0 overflow-hidden">
                <button onClick={() => setOpenCh(openCh === ci ? null : ci)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/50">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: sub?.color }}>{ch.order}</div>
                  <div className="flex-1 min-w-0"><h4 className="font-bold text-mim-navy truncate">{ch.name}</h4><p className="text-xs text-mim-muted">{ch.topics.length} topics</p></div>
                  <Icon name="ChevronDown" className={cn("w-5 h-5 text-mim-muted transition-transform", openCh === ci && "rotate-180")} />
                </button>
                {openCh === ci && (
                  <div className="border-t border-mim-line divide-y divide-mim-line">
                    {ch.topics.map((t) => (
                      <div key={t.id} className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-mim-navy text-sm flex-1 min-w-0">{t.name}</span>
                          {t.has_snapshot ? <Pill tone="green" icon="Check">Snapshot</Pill> : <Pill tone="gray">No snapshot</Pill>}
                          {t.has_video ? <Pill tone="blue" icon="Play">Video</Pill> : null}
                          {t.images > 0 ? <Pill tone="purple" icon="Image">{t.images} img</Pill> : null}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Btn variant="ai" size="sm" onClick={() => genSnapshot(t.id)} disabled={busy === t.id} icon={busy === t.id ? "LoaderCircle" : "Sparkles"} data-testid={`gen-snap-${t.id}`}>
                            <span className={busy === t.id ? "animate-pulse" : ""}>{busy === t.id ? "Generating…" : t.has_snapshot ? "Regenerate snapshot" : "Generate snapshot"}</span>
                          </Btn>
                          <Btn variant="secondary" size="sm" onClick={() => { setMediaFor(mediaFor === t.id ? null : t.id); setMediaVal({ youtube: "", title: "", url: "" }); }} icon="Plus" data-testid={`add-media-${t.id}`}>Add video / image</Btn>
                        </div>
                        {mediaFor === t.id && (
                          <div className="mt-3 rounded-2xl bg-mim-soft p-4 space-y-3 animate-fade-up">
                            <div>
                              <label className="text-xs font-semibold text-mim-navy block mb-1.5">YouTube link or ID</label>
                              <input value={mediaVal.youtube} onChange={(e) => setMediaVal({ ...mediaVal, youtube: e.target.value })} placeholder="https://youtube.com/shorts/..." className={inputCls} data-testid="media-youtube" />
                            </div>
                            <input value={mediaVal.title} onChange={(e) => setMediaVal({ ...mediaVal, title: e.target.value })} placeholder="Title (optional)" className={inputCls} />
                            <div className="flex gap-2"><Btn size="sm" onClick={() => addMedia("video")} disabled={busy === t.id} icon="Youtube" data-testid="save-video">Attach video</Btn></div>
                            <div className="pt-2 border-t border-mim-line">
                              <label className="text-xs font-semibold text-mim-navy block mb-1.5">Or image URL</label>
                              <input value={mediaVal.url} onChange={(e) => setMediaVal({ ...mediaVal, url: e.target.value })} placeholder="https://…/diagram.png" className={inputCls} />
                              <div className="mt-2"><Btn size="sm" variant="secondary" onClick={() => addMedia("image")} disabled={busy === t.id} icon="Image">Add image</Btn></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
